<template>
  <li class="AiReview">
    <div class="AiReview-author">
      <ai-typo variant="paragraph-04-bold" as="span">
        {{ author + ', ' }}
      </ai-typo>
      <ai-typo variant="paragraph-04-regular" as="span">
        {{ typeOfTravel }}
      </ai-typo>
    </div>
    <div class="AiReview-date-content">
      <ai-typo class="AiReview-date" variant="paragraph-04-regular" as="span">
        {{ format(new Date(date ?? ''), ' dd ·  MM ·  yyy') }}
      </ai-typo>
    </div>
    <div class="AiReview-rating">
      <span class="AiReview-star-content">
        <ai-typo as="span" class="sr-only">
          {{
            $t('accessibility.description.hotelRatingOn', { ratingOn: rating })
          }}
        </ai-typo>
        <ai-icon
          v-for="index in activeRating"
          :key="`AiRating-${index}`"
          class="AiReview-star-content--item"
          :name="type"
          :size="size" />
        <ai-icon
          v-if="halfRating"
          class="AiReview-star-content--item AiReview-star-content--item-half"
          :name="`${type}-half` as IconName"
          :size="size" />
        <ai-icon
          v-for="index in inactiveRating"
          :key="`AiRating-${index}`"
          class="AiReview-star-content--item AiReview-star-content--item-empty"
          :name="`${type}-outline` as IconName"
          :size="size" />
      </span>
      <span class="AiReview-reviews-container">
        <ai-typo
          color="gold"
          class="AiReview-score"
          as="span"
          variant="paragraph-04-bold">
          {{ rating + '/5' }}
        </ai-typo>
      </span>
    </div>
    <div
      class="AiReview-description"
      :class="[
        'AiReview-description',
        { 'AiReview-description--expanded': expanded },
      ]">
      <ai-typo as="p" variant="heading-04">{{ title }}</ai-typo>
      <ai-typo
        :id="'description-' + slugify(title)"
        as="p"
        variant="paragraph-04-regular"
        class="AiReview-comment">
        {{ expanded ? description : slicedDescription }}
      </ai-typo>
    </div>
    <ai-button
      v-if="isSlicedReview"
      :class="['AiReview-button', { 'AiReview-button--expanded': expanded }]"
      slim
      :icon-right="expanded ? 'chevron' : 'chevron-down'"
      variant="tertiary"
      :label="buttonLabel"
      :aria-label="
        t('hotel.reviews.button.accessibilityLabel', {
          number: reviewNumber,
          readLabel: buttonLabel,
        })
      "
      :aria-controls="'description-' + slugify(title)"
      :aria-expanded="expanded"
      @click="toggleExpanded" />
    <ai-separator />
  </li>
</template>

<script setup lang="ts">
import { format } from 'date-fns';

import type { IconName } from '../../atoms/AiIcon/types';

import { slugify } from '~~/helpers';

export interface AiReviewProps {
  author: string;
  date: string;
  description: string;
  score: number;
  size?: number;
  title: string;
  type: 'star';
  typeOfTravel: string;
  reviewNumber: number;
}

const props = withDefaults(defineProps<AiReviewProps>(), {
  size: 18,
  type: 'star',
});

const isSlicedReview = computed(() => props.description.length >= 235);
const slicedDescription = computed(() =>
  isSlicedReview.value
    ? props.description.slice(0, 232).concat('...')
    : props.description,
);

const rating = computed(() => Math.floor(props.score * 5) / 100);
const halfRating = computed(() => (rating.value * 10) % 10 === 5);
const activeRating = computed(() => Math.floor(rating.value));
const inactiveRating = computed(
  () => 5 - activeRating.value - (halfRating.value ? 1 : 0),
);

const { t } = useI18n();
const expanded = ref<boolean>(false);
const buttonLabel = computed<string>(() =>
  expanded.value
    ? t('hotel.reviews.button.readLess')
    : t('hotel.reviews.button.readMore'),
);
const toggleExpanded = () => {
  expanded.value = !expanded.value;
};
</script>

<style scoped lang="scss">
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/colors';
@use '@/assets/styles/utilities/mixins';

.AiReview-description {
  margin-bottom: constants.$margin-02;
}

.AiReview-rating {
  display: flex;
  margin-bottom: constants.$inner-01;
}

.AiReview-score {
  margin-left: constants.$inner-03;
}

.AiReview-date-content {
  margin-bottom: constants.$margin-02;
}

.AiReview-date {
  color: colors.$stratos-400;
}

.AiReview-star-content {
  display: flex;
  &--star {
    display: flex;
    margin-right: constants.$inner-03;
  }
  &--item {
    fill: colors.$gold-900;
    &-half {
      fill: none;
    }
    &-empty {
      fill: none;
    }
  }
}
</style>
